import { HyperliquidBaseClient } from '../core/base-client';
import { PrivyHookAdapter } from '../adapters/privy-hook-adapter';
import { PrivyProviderAdapter } from '../adapters/privy-provider-adapter';
import type { ConnectedWallet, PrivySignTypedDataFunction } from '../types/privy';

// Re-export types for public API
export type { ConnectedWallet, PrivySignTypedDataFunction };
export type { ParsedResponse, ParsedOrderStatus } from '../core/utils/response-parser';

export interface BuilderFeeConfig {
  /** Builder wallet address to receive fees */
  address: string;
  /** Fee in tenths of basis point (e.g., 50 = 5bp = 0.05%) */
  fee: number;
}

export interface HyperliquidClientConfig {
  privyWallet: ConnectedWallet;
  signTypedData?: PrivySignTypedDataFunction;
  useDirectProvider?: boolean; // Use provider.request directly instead of signTypedData hook
  isTestnet?: boolean;
  showWalletUIs?: boolean; // Default: false for headless mode
  builder?: BuilderFeeConfig;
}

/**
 * Hyperliquid client for React Web and React Native
 * Works with both @privy-io/react-auth and @privy-io/expo
 */
export class HyperliquidClient extends HyperliquidBaseClient {
  constructor(config: HyperliquidClientConfig) {
    let adapter;

    if (config.useDirectProvider) {
      // Use direct provider.request (like @nktkas SDK does)
      if (!config.privyWallet.getEthereumProvider) {
        throw new Error('privyWallet.getEthereumProvider is required when useDirectProvider is true');
      }
      adapter = new PrivyProviderAdapter({
        getProvider: config.privyWallet.getEthereumProvider.bind(config.privyWallet),
        walletAddress: config.privyWallet.address,
        showWalletUIs: config.showWalletUIs ?? false,
      });
    } else {
      // Use Privy's signTypedData hook
      if (!config.signTypedData) {
        throw new Error('signTypedData is required when useDirectProvider is false');
      }
      adapter = new PrivyHookAdapter({
        signTypedData: config.signTypedData,
        walletAddress: config.privyWallet.address,
        showWalletUIs: config.showWalletUIs ?? false,
      });
    }

    super({
      signingAdapter: adapter,
      isTestnet: config.isTestnet,
      builder: config.builder,
    });
  }

  // All methods inherited from HyperliquidBaseClient
  // Same API works for both React Web and React Native!
}
